/**
 * Branding Presets
 *
 * Built-in preset themes for quick branding setup.
 * Each preset provides colors and fonts that work well together.
 */

import type { BrandingColors, BrandingFonts } from "@tatvex/config";

// ============================================================================
// Types
// ============================================================================

export interface BrandingPreset {
  readonly colors: BrandingColors;
  readonly fonts: BrandingFonts;
}

export type PresetKey = "light" | "dark" | "corporate" | "vibrant";

// ============================================================================
// Presets
// ============================================================================

export const BRANDING_PRESETS: Readonly<Record<PresetKey, BrandingPreset>> = {
  light: {
    colors: {
      primary: "#2563EB",
      accent: "#7C3AED",
      background: "#FFFFFF",
      surface: "#F8FAFC",
      textPrimary: "#0F172A",
      textSecondary: "#64748B",
    },
    fonts: { heading: "Inter", body: "Inter" },
  },
  dark: {
    colors: {
      primary: "#3B82F6",
      accent: "#8B5CF6",
      background: "#0F172A",
      surface: "#1E293B",
      textPrimary: "#F8FAFC",
      textSecondary: "#94A3B8",
    },
    fonts: { heading: "Inter", body: "Inter" },
  },
  corporate: {
    colors: {
      primary: "#1E40AF",
      accent: "#B91C1C",
      background: "#F9FAFB",
      surface: "#FFFFFF",
      textPrimary: "#111827",
      textSecondary: "#6B7280",
    },
    fonts: { heading: "Georgia", body: "Arial" },
  },
  vibrant: {
    colors: {
      primary: "#DC2626",
      accent: "#F59E0B",
      background: "#FFFBEB",
      surface: "#FFFFFF",
      textPrimary: "#1C1917",
      textSecondary: "#78716C",
    },
    fonts: { heading: "Poppins", body: "Open Sans" },
  },
} as const;

/** Preset display labels */
export const PRESET_LABELS: Readonly<Record<PresetKey, string>> = {
  light: "Light",
  dark: "Dark",
  corporate: "Corporate",
  vibrant: "Vibrant",
} as const;

/** All available preset keys */
export const PRESET_KEYS: readonly PresetKey[] = ["light", "dark", "corporate", "vibrant"] as const;
